<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import Menu from "primevue/menu";
import { DirectLink, Participant, Project, Trainer } from "../../../types/PersologOnlineTypes";
import { resolveParticipantLink, sendEmail, sendEmailWithLink } from "../../../utils/EmailUtils";

const { t } = useI18n();

const props = defineProps({
    participant: {
        type: Object as () => Participant,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    directLink: {
        type: Object as () => DirectLink | null,
        default: null,
    },
    // Renders the borderless, rounded variant used inside the mobile participant card.
    card: {
        type: Boolean,
        default: false,
    },
    // Disambiguates DOM ids when the same participant renders more than one instance
    // (e.g. the table row action and the expanded details panel appear together).
    context: {
        type: String,
        default: "",
    },
});

// The "with link" option is only offered once the participant has a link worth
// sending (ACTIVATED/STARTED → questionnaire/direct link, NOTIFIED → download).
const linkAvailable = computed(
    () => resolveParticipantLink(props.participant, props.project, props.directLink) !== null,
);

const idSuffix = computed(() => {
    const contextSuffix = props.context ? `-${props.context}` : "";
    return `${props.project.id}-${props.participant.id}${contextSuffix}`;
});
const buttonId = computed(() => `participant-email-button_btn_${idSuffix.value}`);
const menuId = computed(() => `participant-email-button_menu_${idSuffix.value}`);

const menu = ref();
const toggle = (event: unknown) => {
    menu.value.toggle(event);
};

const items = computed(() => [
    {
        label: t("project-details.participant.email-link.with-link"),
        icon: "pi pi-link",
        disabled: !linkAvailable.value,
        command: () =>
            sendEmailWithLink(props.trainer, props.participant, props.project, props.directLink),
    },
    {
        label: t("project-details.participant.email-link.without-link"),
        icon: "pi pi-envelope",
        command: () => sendEmail(props.project.name, props.trainer, props.participant),
    },
]);
</script>

<template>
    <Button
        :id="buttonId"
        :text="card"
        :rounded="card"
        severity="secondary"
        icon="pi pi-envelope"
        type="button"
        aria-haspopup="true"
        :aria-controls="menuId"
        v-tooltip.top="{ value: t('project-details.participant.tooltips.email'), showDelay: 500 }"
        :aria-label="t('project-details.participant.tooltips.email')"
        @click="toggle"
    />
    <Menu :id="menuId" ref="menu" :model="items" popup />
</template>
